import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Banner,
  Button,
  Card,
  Container,
  Heading,
  HStack,
  Spinner,
  Tabs,
  Text,
  VStack,
} from 'wss3-forge'
import { Laptop24Regular, Mic24Regular } from '@fluentui/react-icons'
import {
  createBridgePairingCode,
  listAuditEvents,
  listBridges,
  revokeBridge,
  type AuditEvent,
  type BridgeDevice,
} from '../lib/api.js'
import { WAKE_EVENT } from '../hooks/useRealtimeEvents.js'

interface WakeEntry {
  bridgeId: string
  at: string
}

type DevicesTab = 'bridges' | 'wake' | 'activity'

function BridgesTab() {
  const qc = useQueryClient()
  const bridgesQuery = useQuery<BridgeDevice[]>({
    queryKey: ['bridges'],
    queryFn: listBridges,
    refetchInterval: 15_000,
  })

  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null)
  const bridgePairing = useMutation({
    mutationFn: createBridgePairingCode,
    onSuccess: (data) => setPairing({ code: data.code, expiresAt: data.expires_at }),
  })
  const bridgeRevoke = useMutation({
    mutationFn: (id: string) => revokeBridge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bridges'] }),
  })

  return (
    <VStack gap="md" fullWidth>
      {bridgesQuery.isLoading && <Spinner size="sm" />}

      {bridgesQuery.data && bridgesQuery.data.length === 0 && (
        <Banner variant="info">
          Aucun appareil appairé. Installe le bridge sur ta machine puis lance
          l’appairage ci-dessous.
        </Banner>
      )}

      {bridgesQuery.data && bridgesQuery.data.length > 0 && (
        <VStack gap="sm" fullWidth>
          {bridgesQuery.data.map((b) => {
            const seen = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0
            const online = seen > Date.now() - 90_000
            const wakeReady = b.capabilities.includes('wake_word')
            return (
              <Card key={b.id} padding="md">
                <VStack gap="sm" fullWidth>
                  <HStack gap="md" align="center" justify="between" fullWidth>
                    <HStack gap="sm" align="center">
                      <Laptop24Regular />
                      <VStack gap="xs" align="start">
                        <HStack gap="xs" align="center">
                          <Text weight="medium">{b.label}</Text>
                          <Badge variant={online ? 'success' : 'default'}>
                            {online ? 'en ligne' : 'hors ligne'}
                          </Badge>
                          {wakeReady && (
                            <Badge variant="default">wake-word</Badge>
                          )}
                        </HStack>
                        <Text size="xs" color="muted">
                          {b.os ?? '?'} · v{b.version ?? '?'} ·{' '}
                          {b.last_seen_at
                            ? `vu ${new Date(b.last_seen_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                            : 'jamais connecté'}
                        </Text>
                      </VStack>
                    </HStack>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => bridgeRevoke.mutate(b.id)}
                      loading={bridgeRevoke.isPending}
                    >
                      Révoquer
                    </Button>
                  </HStack>
                  <Text size="xs" color="muted">
                    Actions disponibles : {b.capabilities.filter((c) => c !== 'wake_word').join(', ') || 'aucune'}
                  </Text>
                </VStack>
              </Card>
            )
          })}
        </VStack>
      )}

      {pairing ? (
        <Banner variant="info" title="Code d'appairage">
          <VStack gap="xs" align="start">
            <Text style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.5rem', letterSpacing: '0.1em' }}>
              {pairing.code}
            </Text>
            <Text size="xs" color="muted">
              Expire à {new Date(pairing.expiresAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}.
              Sur ta machine : <code>stark-bridge pair {pairing.code}</code>
            </Text>
          </VStack>
        </Banner>
      ) : (
        <HStack gap="sm">
          <Button
            variant="primary"
            size="sm"
            onClick={() => bridgePairing.mutate()}
            loading={bridgePairing.isPending}
          >
            Connecter un nouvel appareil
          </Button>
        </HStack>
      )}
    </VStack>
  )
}

function WakeTab() {
  const bridgesQuery = useQuery<BridgeDevice[]>({
    queryKey: ['bridges'],
    queryFn: listBridges,
  })

  const [wakeLog, setWakeLog] = useState<WakeEntry[]>([])
  useEffect(() => {
    const onWake = (ev: Event): void => {
      const detail = (ev as CustomEvent<{ bridgeId?: string; at?: string }>).detail
      if (!detail) return
      const entry: WakeEntry = {
        bridgeId: detail.bridgeId ?? '?',
        at: detail.at ?? new Date().toISOString(),
      }
      setWakeLog((prev) => [entry, ...prev].slice(0, 10))
    }
    window.addEventListener(WAKE_EVENT, onWake)
    return () => window.removeEventListener(WAKE_EVENT, onWake)
  }, [])

  const wakeBridges = (bridgesQuery.data ?? []).filter((b) => b.capabilities.includes('wake_word'))

  return (
    <VStack gap="md" fullWidth>
      <Text size="sm">
        Le mot de réveil <strong>"Stark"</strong> est détecté localement par chaque
        appareil via Vosk. L’audio ne quitte jamais la machine. Quand tu dis "Stark",
        l’app entre automatiquement en mode vocal.
      </Text>

      {bridgesQuery.data && wakeBridges.length === 0 && (
        <Banner variant="warning">
          Aucun appareil n’a le wake-word activé. Sur ton bridge :
          <code> stark-bridge wake-init</code> puis <code>stark-bridge wake-on</code>,
          puis relance le bridge.
        </Banner>
      )}

      {wakeBridges.length > 0 && (
        <Banner variant="success">
          Wake-word actif sur : {wakeBridges.map((b) => b.label).join(', ')}.
        </Banner>
      )}

      <VStack gap="sm" fullWidth>
        <Text size="sm" weight="medium">Activations récentes</Text>
        {wakeLog.length === 0 ? (
          <Banner variant="info">Aucune activation depuis l'ouverture de cette page.</Banner>
        ) : (
          <VStack gap="xs" fullWidth>
            {wakeLog.map((w, i) => (
              <Card key={`${w.at}-${i}`} variant="outlined" padding="sm">
                <HStack gap="sm" align="center">
                  <Mic24Regular style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  <Text size="sm" style={{ fontFamily: 'ui-monospace, monospace' }}>
                    {new Date(w.at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </Text>
                  <Text size="xs" color="muted">
                    bridge {w.bridgeId.slice(0, 8)}…
                  </Text>
                </HStack>
              </Card>
            ))}
          </VStack>
        )}
      </VStack>
    </VStack>
  )
}

function ActivityTab() {
  const auditQuery = useQuery<AuditEvent[]>({
    queryKey: ['audit'],
    queryFn: () => listAuditEvents(30),
    refetchInterval: 30_000,
  })

  return (
    <VStack gap="md" fullWidth>
      <Text size="sm" color="muted">
        Les 30 dernières actions exécutées par Stark. Vert pour les succès, rouge
        pour les échecs. Le badge "destructif" signale une action sensible.
      </Text>
      {auditQuery.isLoading && <Spinner size="sm" />}
      {auditQuery.data && auditQuery.data.length === 0 && (
        <Banner variant="info">Aucune activité enregistrée.</Banner>
      )}
      {auditQuery.data && auditQuery.data.length > 0 && (
        <VStack gap="xs" fullWidth>
          {auditQuery.data.map((ev) => {
            const safetyVariant: 'default' | 'warning' | 'error' =
              ev.safety === 'destructive' ? 'error' : ev.safety === 'confirm' ? 'warning' : 'default'
            const safetyLabel = ev.safety === 'destructive'
              ? 'destructif'
              : ev.safety === 'confirm'
                ? 'effet ext.'
                : 'lecture'
            return (
              <Card key={ev.id} variant="outlined" padding="sm">
                <VStack gap="xs" align="start" fullWidth>
                  <HStack gap="xs" align="center" wrap>
                    <Text weight="medium" style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}>
                      {ev.tool}
                    </Text>
                    <Badge variant={safetyVariant}>{safetyLabel}</Badge>
                    {ev.ok ? (
                      <Badge variant="success">ok</Badge>
                    ) : (
                      <Badge variant="error">échec</Badge>
                    )}
                  </HStack>
                  <Text size="xs" color="muted">
                    {new Date(ev.at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    {ev.duration_ms != null ? ` · ${ev.duration_ms} ms` : ''}
                    {ev.error ? ` · ${ev.error.slice(0, 80)}` : ''}
                  </Text>
                </VStack>
              </Card>
            )
          })}
        </VStack>
      )}
    </VStack>
  )
}

export function DevicesPage() {
  const [tab, setTab] = useState<DevicesTab>('bridges')

  return (
    <Container maxWidth="md" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Appareils</Heading>
          <Text color="muted">
            Tes machines liées à Stark via le Stark Bridge, le mot de réveil et l’activité
            récente.
          </Text>
        </VStack>

        <Tabs
          tabs={[
            { id: 'bridges', label: 'Bridges' },
            { id: 'wake', label: 'Mot de réveil' },
            { id: 'activity', label: 'Activité' },
          ]}
          value={tab}
          onChange={(id) => setTab(id as DevicesTab)}
          variant="underline"
        />

        {tab === 'bridges' && <BridgesTab />}
        {tab === 'wake' && <WakeTab />}
        {tab === 'activity' && <ActivityTab />}
      </VStack>
    </Container>
  )
}
